import { ANALYTICS_ACCOUNTS_DASHBOARD, ANALYTICS_ACCOUNTS_DATA } from 'constants/types';
import { convertSnakeCaseKeysToCamelCase } from 'utils';
import { IAccountsDashboard } from './interface';

export interface IAccount {
  companyId: number;
  companyName: string;
  companyLogo: string;
  industry: string;
  targetedContacts: number;
  totalSent: number;
  sentPercentage: number;
  totalOpens: number;
  totalClicks: number;
  uniqueOpens: number;
  uniqueClicks: number;
  lifetimeScore: number;
  campaignsScore: number;
}

export interface IAnalyticsAccounts {
  data: IAccount[];
  loading: boolean;
  loaded: boolean;
  selectedAccount: string;
  totalAccounts: number;
  redirectUrl: string;
  searchReset: boolean;
  filterReset: boolean;
  accountsByFilter: string;
}

const initialAnalyticsAccounts = {
  data: Array<IAccount>(),
  loading: false,
  loaded: false,
  selectedAccount: '',
  totalAccounts: 0,
  redirectUrl: '',
  searchReset: false,
  filterReset: false,
  accountsByFilter: '',
};

export const analyticsAccountsReducer = (state: IAnalyticsAccounts = initialAnalyticsAccounts, action: any) => {
  const newState = { ...state };
  switch (action.type) {
    case ANALYTICS_ACCOUNTS_DATA.LOAD:
      newState.loading = true;
      newState.loaded = false;
      newState.data = [];
      newState.totalAccounts = 0;
      break;
    case ANALYTICS_ACCOUNTS_DATA.LOAD_SUCCESS:
      newState.loading = false;
      newState.loaded = true;
      if (action.payload.result !== undefined)
        newState.data = action.payload.result.map((account: IAccount) => convertSnakeCaseKeysToCamelCase(account));
      else newState.data = [];
      newState.totalAccounts = action.payload.total_records;
      break;
    case ANALYTICS_ACCOUNTS_DATA.LOAD_MORE:
      newState.loading = true;
      newState.loaded = false;
      break;
    case ANALYTICS_ACCOUNTS_DATA.LOAD_MORE_SUCCESS:
      newState.loading = false;
      newState.loaded = true;
      if (action?.payload?.result?.length > 0) {
        newState.data = [
          ...newState.data,
          ...action.payload?.result?.map((account: IAccount) => convertSnakeCaseKeysToCamelCase(account)),
        ];
      }
      break;

    case ANALYTICS_ACCOUNTS_DATA.SEARCH_RESET:
      newState.searchReset = action.payload;
      return newState;
    case ANALYTICS_ACCOUNTS_DATA.FILTER_RESET:
      newState.filterReset = action.payload;
      return newState;
    case ANALYTICS_ACCOUNTS_DATA.SET_ACCOUNTS_BY_FILTER:
      newState.accountsByFilter = action.payload;
      return newState;

    case ANALYTICS_ACCOUNTS_DATA.LOAD_FAIL:
      newState.loading = false;
      newState.loaded = false;
      break;
    case ANALYTICS_ACCOUNTS_DATA.RESET:
      newState.loading = false;
      newState.loaded = false;
      newState.data = [];
      newState.accountsByFilter = '';
      newState.totalAccounts = 0;

      break;
    case ANALYTICS_ACCOUNTS_DATA.SELECTED_ACCOUNT:
      newState.selectedAccount = action.payload.selectedAccount;
      break;
    case ANALYTICS_ACCOUNTS_DATA.SELECTED_ACCOUNT_RESET:
      newState.selectedAccount = '';
      break;

    case ANALYTICS_ACCOUNTS_DATA.SET_REDIRECT_URL:
      newState.redirectUrl = action.payload.redirectUrl;
      break;
    case ANALYTICS_ACCOUNTS_DATA.RESET_REDIRECT_URL:
      newState.redirectUrl = '';
      break;
    default:
      break;
  }
  return newState;
};

//Accounts dashboard analytics
const initialAccountsDashboard = {
  data: {} as IAccountsDashboard,
  loading: false,
  page: 0,
  total_contacts: 0,
  campaignIds: [],
  emailIds: [],
  contactsOnly: false,
  redirectUrl: '',
  email_count: 0,
  campaign_count: 0,
  loadingDasboard: false,
};

export const accountsDashboardReducer = (state = initialAccountsDashboard, action: any) => {
  const newState = { ...state };
  switch (action.type) {
    case ANALYTICS_ACCOUNTS_DASHBOARD.LOAD_STATS:
      newState.loadingDasboard = true;
      newState.contactsOnly = action.payload;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.LOAD:
      newState.loading = true;
      newState.contactsOnly = action.payload;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.SHOW_ENTITIES_COUNT:
      newState.campaign_count = action.payload.campaign_count;
      newState.email_count = action.payload.email_count;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.DATA:
      newState.data.account_stats = action.payload.data.account_stats;
      newState.data.company_id = action.payload.data.company_id;
      newState.data.company_name = action.payload.data.company_name;
      newState.data.company_sales_app_url = action.payload.data.company_sales_app_url;
      newState.data.company_website = action.payload.data.company_website;
      newState.data.engagement_score = action.payload.data.engagement_score;
      newState.data.thumbnail = action.payload.data.thumbnail;
      newState.loadingDasboard = false;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.LOAD_SUCCESS:
      newState.loading = false;
      if (state.total_contacts !== action.payload.total_records) {
        newState.total_contacts = action.payload.total_records;
      }
      newState.data.account_contacts =
        newState.page > 0
          ? [...state.data.account_contacts, ...action.payload.data.account_contacts]
          : action.payload.data.account_contacts;
      if (action.payload.data.account_contacts !== undefined && action.payload.data.account_contacts.length > 0) {
        newState.page = state.page + 1;
      }
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.LOAD_FAIL:
      newState.loading = false;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.LOAD_STATS_FAIL:
      newState.loadingDasboard = false;
      return newState;

    case ANALYTICS_ACCOUNTS_DASHBOARD.SET_SELECTED_CAMPAIGNS:
      newState.campaignIds = action.payload.campaignIds;
      return newState;

    case ANALYTICS_ACCOUNTS_DASHBOARD.SET_SELECTED_EMAILS:
      newState.emailIds = action.payload.emailIds;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.RESET:
      if (action.payload) newState.data.account_contacts = [];
      else newState.data = {};
      newState.page = 0;
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.RESET_REDIRECT_URL:
      newState.redirectUrl = '';
      return newState;
    case ANALYTICS_ACCOUNTS_DASHBOARD.SET_REDIRECT_URL:
      newState.redirectUrl = action.payload.redirectUrl;
      return newState;
    default:
      return newState;
  }
  return newState;
};
